/**
 * Post-order “complete prescription” page only — loaded from `rx-customer-prescription-completion`.
 * Patches `<rx-configurator>` from {@link ./rx-customer-config-core.js} (PDP uses `rx-config.js` only).
 */
(function () {
  const ATTR = "data-customer-completion";
  /** Primary action on email completion page (shared CSS still uses .add-to-cart-button). */
  const SUBMIT_PRESCRIPTION_BTN =
    '[data-action="submit-prescription"], [data-action="add-to-cart"]';

  function isCustomerRoot(el) {
    return el && el.tagName === "RX-CONFIGURATOR" && el.getAttribute(ATTR) === "true";
  }

  function applyLensTypePreset(cfg) {
    const fromAttr = (cfg.dataset.initialLensType || "").trim();
    let fromUrl = "";
    try {
      const q = new URLSearchParams(window.location.search).get("lens_type");
      if (q) fromUrl = decodeURIComponent(q).trim();
    } catch (_) {
      /* ignore */
    }
    let fallback = (cfg.dataset.fallbackLensType || "").trim();
    if (!fallback) {
      try {
        const types = JSON.parse(cfg.dataset.prescriptionTypes || "[]");
        const sorted = [...types].sort(
          (a, b) => (Number(a.display_order) || 0) - (Number(b.display_order) || 0)
        );
        fallback = (sorted[0]?.title && String(sorted[0].title).trim()) || "";
      } catch (_) {
        /* ignore */
      }
    }
    const preset = fromUrl || fromAttr || fallback;
    if (preset) {
      cfg.formData.lensType = preset;
      cfg.selectedType = preset;
    }
  }

  /** Skip lens config / review steps; RX-only flow matches public API payload. */
  function patchViewsObjectForCustomer(cfg) {
    const vo = cfg.viewsObject;
    if (!vo) return;
    vo[1].skipIf = () => true;
    vo[4].skipIf = () => true;
    vo[5].skipIf = () => true;
    vo[6].skipIf = () => true;
    vo[7].skipIf = () => true;
    vo[8].skipIf = () => true;
    vo[9].skipIf = () => true;
  }

  function refreshFooterRefs(cfg) {
    const footer = cfg.querySelector(".prescription-entry-footer");
    if (!footer) return;
    cfg.continueButton = footer.querySelector('[data-action="continue"]');
    const submitBtn = footer.querySelector(SUBMIT_PRESCRIPTION_BTN);
    cfg.submitPrescriptionButton = submitBtn;
    cfg.addToCartButton = submitBtn;
  }

  /** After core showView (PDP rules), fix Continue vs Submit + back visibility for completion. */
  function applyCustomerFooterButtons(cfg, viewObject) {
    const buttonContainer = cfg.querySelector(".prescription-entry-footer .button-container");
    if (!buttonContainer) return;
    const isRxEnd = viewObject.step === 3;
    const isFinalStep =
      Object.keys(cfg.viewsObject).length == viewObject.step || isRxEnd;
    if (viewObject.step === 1) {
      cfg.backButton?.classList.add("hidden");
      cfg.submitPrescriptionButton?.classList.add("hidden");
      buttonContainer.classList.remove("half-width");
    } else if (isFinalStep) {
      cfg.submitPrescriptionButton?.classList.remove("hidden");
      cfg.continueButton?.classList.add("hidden");
      if (isRxEnd) cfg.backButton?.classList.remove("hidden");
    } else {
      cfg.backButton?.classList.remove("hidden");
      if (viewObject.step === 2) cfg.backButton?.classList.add("hidden");
      cfg.submitPrescriptionButton?.classList.add("hidden");
      cfg.continueButton?.classList.remove("hidden");
      buttonContainer.classList.add("half-width");
    }
  }

  function navigateToFirstApplicableStep(cfg) {
    const maxStep = Object.keys(cfg.viewsObject).length;
    let n = 1;
    let firstView = cfg.viewsObject[1];
    while (n <= maxStep) {
      const step = cfg.viewsObject[n];
      if (!step) break;
      if (typeof step.skipIf !== "function" || !step.skipIf()) {
        firstView = step;
        break;
      }
      n++;
    }
    if (firstView && firstView.container) {
      cfg.showView(firstView);
      cfg.positionProgressBar(firstView);
      cfg.updateProgress({ step: firstView.step });
    }
    const progressBar = cfg.querySelector(".rx-top-progress-bar");
    if (progressBar) {
      progressBar.style.display = "block";
      progressBar.style.visibility = "visible";
    }
    cfg.updateToSelectedVariantData();
  }

  /** Prescription panel is shown (same idea as PDP step 3), even if wizard step index drifts. */
  function customerCompletionPrescriptionVisible(cfg) {
    const form = cfg.querySelector("#prescriptionFormView");
    return !!(form && !form.classList.contains("hidden"));
  }

  /**
   * Storefront-only URL: `{app_proxy_prefix}/{subpath}/prescription-submit` (same origin), e.g. `/a/prescriptions/prescription-submit`.
   * Uses `data-app-api-base` from section settings when set; otherwise `/a/{subpath}/…` (prefix `a` matches shopify.app.toml [app_proxy].prefix).
   */
  function customerCompletionPrescriptionSubmitUrl(cfg) {
    const rawBase = (cfg.getAttribute("data-app-api-base") || "").trim();
    if (rawBase.startsWith("/")) {
      const base = rawBase.replace(/\/+$/, "");
      return `${base}/prescription-submit`;
    }
    if (rawBase.startsWith("http")) {
      try {
        const u = new URL(rawBase);
        if (u.origin === window.location.origin) {
          const p = u.pathname.replace(/\/+$/, "") || "";
          return `${p}/prescription-submit`;
        }
      } catch (_) {
        /* ignore */
      }
    }
    const subpath = (
      cfg.getAttribute("data-app-proxy-subpath") ||
      cfg.getAttribute("data-app-proxy-prefix") ||
      "prescriptions"
    )
      .trim()
      .replace(/^\/+|\/+$/g, "");
    const safe = subpath || "prescriptions";
    return `/a/${safe}/prescription-submit`;
  }

  /** Footer region above Continue / Submit — all RX validation + submit feedback on completion page. */
  function customerCompletionMessageSlot(cfg) {
    return cfg.querySelector("[data-rx-customer-message-slot]");
  }

  /** Core inserts `.prescription-validation-error` under `.button-container`; move it into the slot. */
  function movePrescriptionValidationErrorIntoSlot(cfg) {
    const slot = customerCompletionMessageSlot(cfg);
    if (!slot || !isCustomerRoot(cfg)) return;
    const errEl = cfg.querySelector(".prescription-validation-error");
    if (!errEl) return;
    slot.querySelectorAll(".prescription-validation-error").forEach((el) => {
      if (el !== errEl) el.remove();
    });
    slot.appendChild(errEl);
  }

  /**
   * Mirrors choose-lens / PDP validation behavior without editing rx-config.js:
   * inline errors only on the completion flow, PDF required when method is upload.
   */
  function installCustomerCompletionValidationPatches(Ctor) {
    if (Ctor.prototype._rxCustomerCompletionValidationPatched) return;
    Ctor.prototype._rxCustomerCompletionValidationPatched = true;

    const origValidate = Ctor.prototype.validatePrescription;
    Ctor.prototype.validatePrescription = function (showErrors) {
      if (!isCustomerRoot(this)) {
        return origValidate.call(this, showErrors);
      }
      const savedStep = this.currentStep;
      const needFakeStep =
        customerCompletionPrescriptionVisible(this) && savedStep !== 3;
      if (needFakeStep) {
        this.currentStep = 3;
      }
      try {
        // validatePrescription() itself now enforces a file/photo for upload
        // and camera methods (with the same messaging), so no extra check
        // is needed here.
        return origValidate.call(this, showErrors);
      } finally {
        if (needFakeStep) {
          this.currentStep = savedStep;
        }
      }
    };

    const origShowRxErr = Ctor.prototype.showPrescriptionError;
    Ctor.prototype.showPrescriptionError = function (message, errorFields) {
      if (
        isCustomerRoot(this) &&
        customerCompletionPrescriptionVisible(this) &&
        this.currentStep !== 3
      ) {
        const s = this.currentStep;
        this.currentStep = 3;
        try {
          origShowRxErr.call(this, message, errorFields || []);
        } finally {
          this.currentStep = s;
        }
      } else {
        origShowRxErr.call(this, message, errorFields);
      }
      if (isCustomerRoot(this) && customerCompletionPrescriptionVisible(this)) {
        movePrescriptionValidationErrorIntoSlot(this);
        const slot = customerCompletionMessageSlot(this);
        const scrollEl = slot || this.querySelector(".prescription-validation-error");
        if (scrollEl) {
          requestAnimationFrame(() => {
            scrollEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
          });
        }
      }
    };
  }

  /** Prism summary errors: render in message slot above buttons on completion page (not beside prism UI). */
  function installRxPrescriptionCompletionPatches() {
    const Tag = customElements.get("rx-prescription");
    if (!Tag || Tag.prototype._rxCustomerRxPrescriptionMessagesPatched) return;
    Tag.prototype._rxCustomerRxPrescriptionMessagesPatched = true;

    const origShowPrism = Tag.prototype.showPrismSummaryError;
    Tag.prototype.showPrismSummaryError = function (message) {
      const cfg = this.closest && this.closest(`rx-configurator[${ATTR}="true"]`);
      const slot = cfg ? customerCompletionMessageSlot(cfg) : null;
      if (slot) {
        slot.querySelectorAll(".prism-summary-error").forEach((el) => el.remove());
        const summaryError = document.createElement("div");
        summaryError.className = "prism-summary-error";
        summaryError.setAttribute("role", "alert");
        summaryError.textContent = message;
        slot.appendChild(summaryError);
        requestAnimationFrame(() => {
          slot.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
        return;
      }
      return origShowPrism.call(this, message);
    };

    const origHidePrism = Tag.prototype.hidePrismSummaryError;
    Tag.prototype.hidePrismSummaryError = function () {
      const cfg = this.closest && this.closest(`rx-configurator[${ATTR}="true"]`);
      const slot = cfg ? customerCompletionMessageSlot(cfg) : null;
      if (slot) {
        slot.querySelectorAll(".prism-summary-error").forEach((el) => el.remove());
      }
      return origHidePrism.call(this);
    };
  }

  function retrofitCustomerConfigurator(cfg) {
    if (!isCustomerRoot(cfg) || cfg._rxCustomerRetrofitApplied) return;
    cfg._rxCustomerRetrofitApplied = true;
    applyLensTypePreset(cfg);
    patchViewsObjectForCustomer(cfg);
    const lt = cfg.formData.lensType;
    if (lt && String(lt).trim() !== "") {
      cfg.querySelectorAll("#prescriptionTypeView .prescription-option").forEach((opt) => {
        opt.classList.toggle("selected", opt.dataset.type === lt);
      });
    }
    navigateToFirstApplicableStep(cfg);
  }

  /* ----- App API: submit prescription (intercepts shared configurator “final submit” handler) ----- */

  function linkTokensFromQuery() {
    const p = new URLSearchParams(window.location.search);
    const single = (p.get("token") || "").trim();
    const order = (p.get("order_token") || "").trim();
    return {
      singleToken: single ? single : null,
      orderToken: order ? order : null,
    };
  }

  function customerCompletionButtonContainer(cfg) {
    return cfg.querySelector(".prescription-entry-footer .button-container");
  }

  /** Prefer `[data-rx-customer-message-slot]`; fall back to inserting inside `.button-container` if section is older. */
  function appendToCustomerMessageSlot(cfg, el) {
    const slot = customerCompletionMessageSlot(cfg);
    if (slot) {
      slot.appendChild(el);
      return slot;
    }
    const bc = customerCompletionButtonContainer(cfg);
    if (bc) {
      bc.insertBefore(el, bc.firstChild);
      return bc;
    }
    return null;
  }

  function clearCustomerSubmitFeedback(cfg) {
    const slot = customerCompletionMessageSlot(cfg);
    if (slot) {
      slot.querySelectorAll(".rx-customer-submit-banner").forEach((e) => e.remove());
    } else {
      const bc = customerCompletionButtonContainer(cfg);
      bc?.querySelectorAll(".rx-customer-submit-banner").forEach((e) => e.remove());
    }
  }

  function showCustomerSubmitSuccess(cfg, messageText) {
    clearCustomerSubmitFeedback(cfg);
    if (typeof cfg.clearPrescriptionError === "function") cfg.clearPrescriptionError();
    const wrap = document.createElement("div");
    wrap.className = "rx-customer-submit-banner rx-customer-submit-banner--success";
    wrap.setAttribute("role", "status");
    const p = document.createElement("p");
    p.className = "rx-customer-submit-banner__text";
    p.textContent = messageText;
    wrap.appendChild(p);
    const p2 = document.createElement("p");
    p2.className = "rx-customer-submit-banner__actions";
    const a = document.createElement("a");
    const home = (cfg.getAttribute("data-home-url") || "/").trim() || "/";
    a.href = home;
    a.textContent = "Back to homepage";
    p2.appendChild(a);
    wrap.appendChild(p2);
    const anchor = appendToCustomerMessageSlot(cfg, wrap);
    requestAnimationFrame(() => {
      anchor?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }

  function showCustomerSubmitError(cfg, messageText) {
    clearCustomerSubmitFeedback(cfg);
    const wrap = document.createElement("div");
    wrap.className = "rx-customer-submit-banner rx-customer-submit-banner--error";
    wrap.setAttribute("role", "alert");
    const p = document.createElement("p");
    p.className = "rx-customer-submit-banner__text";
    p.textContent = messageText;
    wrap.appendChild(p);
    const anchor = appendToCustomerMessageSlot(cfg, wrap);
    requestAnimationFrame(() => {
      anchor?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }

  function buildPrescriptionJson(cfg) {
    const p = cfg.formData.prescription || {};
    const r = p.right || {};
    const l = p.left || {};
    const axisR = r.AXIS != null && r.AXIS !== "" ? r.AXIS : r.AXI != null ? r.AXI : "";
    const axisL = l.AXIS != null && l.AXIS !== "" ? l.AXIS : l.AXI != null ? l.AXI : "";
    return {
      right: { SPH: r.SPH ?? "", CYL: r.CYL ?? "", AXIS: axisR },
      left: { SPH: l.SPH ?? "", CYL: l.CYL ?? "", AXIS: axisL },
      PD: p.isSplitPd ? "" : p.PD ?? "",
      PD_right: p.isSplitPd ? p.PD_right ?? "" : "",
      PD_left: p.isSplitPd ? p.PD_left ?? "" : "",
      ADD: p.ADD ?? "",
      isSplitPd: !!p.isSplitPd,
      isSamePrescription: !!p.isSamePrescription,
      addPrism: !!p.isAddPrism,
      prism: p.isAddPrism && p.prism && typeof p.prism === "object" ? p.prism : {},
      notes: p.notes ?? "",
      prescriptionMethod: cfg.formData.prescriptionMethod,
      pdfUrl: cfg.formData.pdfUrl ?? null,
    };
  }


  /** Accepts `gid://shopify/GenericFile/...` or `FILE_PENDING_gid://shopify/GenericFile/...`. */
  function normalizePrescriptionPdfFileId(rawValue) {
    if (rawValue == null) return null;
    const raw = String(rawValue).trim();
    if (!raw) return null;
    if (raw.startsWith("gid://shopify/GenericFile/")) return raw;
    const pendingPrefix = "FILE_PENDING_";
    if (raw.startsWith(pendingPrefix)) {
      const stripped = raw.slice(pendingPrefix.length).trim();
      return stripped.startsWith("gid://shopify/GenericFile/") ? stripped : null;
    }
    return null;
  }

  /**
   * @param {{ singleToken: string | null; orderToken: string | null }} linkCtx
   */
  async function submitCustomerRx(cfg, linkCtx) {
    const method = String(cfg.formData.prescriptionMethod || "").toLowerCase();
    if (method !== "manual" && method !== "upload" && method !== "camera") {
      throw new Error("Choose manual entry, upload RX, or take a picture of your RX to continue.");
    }
    const rxPrescription = cfg.querySelector("rx-prescription");
    if (rxPrescription && rxPrescription.handlePrescriptionUpdate) {
      rxPrescription.handlePrescriptionUpdate();
    }
    if (typeof cfg.validatePrescription === "function" && !cfg.validatePrescription(true)) {
      return { __inlineValidationFailed: true };
    }

    const isFileMethod = method === "upload" || method === "camera";
    const pdfFileIdForApi = isFileMethod
      ? normalizePrescriptionPdfFileId(cfg.formData.pdfUrl)
      : null;
    const pdfUrlForApi =
      isFileMethod && cfg.formData.pdfUrl
        ? String(cfg.formData.pdfUrl).trim()
        : null;
    const prescriptionJson = buildPrescriptionJson(cfg);
    const url = customerCompletionPrescriptionSubmitUrl(cfg);
    const payload = {
      prescriptionMethod: method,
      prescriptionJson,
      pdfUrl: isFileMethod ? pdfUrlForApi : null,
      pdfFileId: isFileMethod ? pdfFileIdForApi : null,
    };
    if (linkCtx.singleToken) payload.token = linkCtx.singleToken;
    if (linkCtx.orderToken) payload.orderToken = linkCtx.orderToken;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || res.statusText || "Submit failed");
    return data;
  }

  async function runCustomerPrescriptionSubmit(cfg) {
    const linkCtx = linkTokensFromQuery();
    const submitUrl = customerCompletionPrescriptionSubmitUrl(cfg);
    const tokenField = cfg.querySelector("[data-rx-customer-token]");
    const baseField = cfg.querySelector("[data-rx-customer-app-base]");
    if (tokenField) tokenField.value = linkCtx.singleToken || "";
    if (baseField) baseField.value = submitUrl;

    clearCustomerSubmitFeedback(cfg);

    if (!linkCtx.singleToken && !linkCtx.orderToken) {
      showCustomerSubmitError(
        cfg,
        "This page needs the link from your email (missing token or order_token in the URL).",
      );
      return;
    }
    const btn = cfg.submitPrescriptionButton || cfg.addToCartButton;
    const spin = btn && btn.querySelector(".loading-spinner");
    const txt =
      btn &&
      (btn.querySelector(".rx-submit-prescription-label") ||
        btn.querySelector(".add-to-cart-button-text"));
    const labelTextBefore = txt ? String(txt.textContent || "").trim() : "";
    if (btn) {
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
    }
    if (spin) {
      spin.classList.remove("hidden");
      if (txt) txt.classList.add("hidden");
    } else if (txt) {
      txt.textContent = "Submitting…";
    }
    try {
      const submitResult = await submitCustomerRx(cfg, linkCtx);
      if (submitResult && submitResult.__inlineValidationFailed) {
        return;
      }
      if (submitResult && submitResult.dryRun) {
        showCustomerSubmitSuccess(
          cfg,
          submitResult.message ||
            "RX_CUSTOMER_PRESCRIPTION_SUBMIT_DRY_RUN is enabled: request is valid; no Shopify order or database changes were made.",
        );
      } else if (submitResult && submitResult.multi === true && Number(submitResult.lineItemCount) > 1) {
        showCustomerSubmitSuccess(
          cfg,
          `Thank you! Your prescription was submitted for ${submitResult.lineItemCount} lens lines.`,
        );
      } else {
        showCustomerSubmitSuccess(cfg, "Thank you! Your prescription was submitted.");
      }
    } catch (err) {
      showCustomerSubmitError(cfg, err && err.message ? err.message : "Submit failed");
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute("aria-busy");
      }
      if (spin) spin.classList.add("hidden");
      if (txt) {
        txt.classList.remove("hidden");
        if (!spin && labelTextBefore) txt.textContent = labelTextBefore;
      }
    }
  }

  function installConfiguratorPatches() {
    const Ctor = customElements.get("rx-configurator");
    if (!Ctor || Ctor.prototype._rxCustomerCompletionInstalled) return;
    Ctor.prototype._rxCustomerCompletionInstalled = true;

    installCustomerCompletionValidationPatches(Ctor);

    const origConfiguratorSubmit = Ctor.prototype.handleAddToCart;
    Ctor.prototype.handleAddToCart = function () {
      if (isCustomerRoot(this)) {
        void runCustomerPrescriptionSubmit(this);
        return;
      }
      return origConfiguratorSubmit.call(this);
    };

    const origConnected = Ctor.prototype.connectedCallback;
    Ctor.prototype.connectedCallback = function () {
      origConnected.call(this);
      if (isCustomerRoot(this)) retrofitCustomerConfigurator(this);
    };

    const origShowView = Ctor.prototype.showView;
    Ctor.prototype.showView = function (viewObject) {
      if (isCustomerRoot(this)) refreshFooterRefs(this);
      origShowView.call(this, viewObject);
      if (isCustomerRoot(this)) applyCustomerFooterButtons(this, viewObject);
    };

    const origGetVariant = Ctor.prototype.getCurrentSelectedVariant;
    Ctor.prototype.getCurrentSelectedVariant = function () {
      if (!isCustomerRoot(this)) return origGetVariant.call(this);
      const variants = this.product?.variants;
      try {
        const q = new URLSearchParams(window.location.search).get("variant");
        if (q && variants?.length) {
          const want = String(q).trim();
          const v = variants.find((x) => String(x.id) === want);
          if (v) return v;
        }
      } catch (_) {
        /* ignore */
      }
      const fromAttr = (this.dataset.variant || "").trim();
      if (fromAttr && variants?.length) {
        const v = variants.find((x) => String(x.id) === fromAttr);
        if (v) return v;
      }
      if (variants?.length) return variants[0];
      return { price: 0, id: null };
    };

    document.querySelectorAll(`rx-configurator[${ATTR}="true"]`).forEach((el) =>
      retrofitCustomerConfigurator(el)
    );
  }

  /** Hidden fields for debugging / future use. */
  function initCustomerSubmit(root) {
    if (!isCustomerRoot(root) || root._rxCustomerSubmitInit) return;
    root._rxCustomerSubmitInit = true;
    const linkCtx = linkTokensFromQuery();
    const submitUrl = customerCompletionPrescriptionSubmitUrl(root);
    const tokenField = root.querySelector("[data-rx-customer-token]");
    const baseField = root.querySelector("[data-rx-customer-app-base]");
    if (tokenField) tokenField.value = linkCtx.singleToken || "";
    if (baseField) baseField.value = submitUrl;
  }

  function boot() {
    installRxPrescriptionCompletionPatches();
    installConfiguratorPatches();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.querySelectorAll(`rx-configurator[${ATTR}="true"]`).forEach((root) => {
          initCustomerSubmit(root);
        });
      });
    });
  }

  if (customElements.get("rx-configurator")) {
    boot();
  } else {
    customElements.whenDefined("rx-configurator").then(boot);
  }
})();
